import React, { useContext, useEffect, useRef, useState } from 'react'
import { BiLoaderAlt } from 'react-icons/bi'
import { FaCheckCircle } from 'react-icons/fa'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ImBulkAddStock.module.css'

const ImBulkAddStock = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier

    const [productsData, setProductsData] = useState([])
    const [filteredData, setFilteredData] = useState([])
    const [selectedItems, setSelectedItems] = useState([]) // array of selected rows
    const [shouldUploadNow, setShouldUploadNow] = useState(false)
    const [isUpdatingStock, setIsUpdatingStock] = useState(false)
    const getInventoryProducts = async () => {
        const response = await fetch(BASE_URL_API + "getInventoryStock",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setProductsData(respo?.response)
                setFilteredData(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                }
                setProductsData([])
                setFilteredData([])
            }
            console.log(respo)
        }
    }

    useEffect(() => {
        getInventoryProducts()
    }, [activeStore?.storeIdentifier])

    const filterData = (data, searchKey) => {
        setFilteredData(data?.filter(item =>
            Object.values(item).some(value =>
                value?.toString().toLowerCase().includes(searchKey.toLowerCase())
            )
        ))
    }

    // Toggle select/deselect product
    const toggleSelect = (item) => {
        const exists = selectedItems.find(p => p.productIdentifier === item.productIdentifier)
        if (exists) {
            setSelectedItems(selectedItems.filter(p => p.productIdentifier !== item.productIdentifier))
        } else {
            setSelectedItems([...selectedItems, { ...item, addQuantity: 0 }])
        }
    }

    // Handle quantity update for selected item
    const handleQuantityChange = (id, value) => {
        setSelectedItems(prev =>
            prev.map(item => {
                if (item.productIdentifier === id) {
                    const addQuantity = Number(value) || 0;
                    const baseQuantity = item?.quantity ?? 0;
                    return {
                        ...item,
                        quantity: parseFloat(baseQuantity), // force null → 0
                        addQuantity,
                        finalQuantity: parseFloat(baseQuantity) + parseFloat(addQuantity)
                    };
                }
                return item;
            })
        );
    };

    // keep refs for inputs
    const inputRefs = useRef([])

    // keyboard navigation handler
    const handleKeyDown = (e, index) => {
        if (e.key === "Enter" || e.key === "ArrowDown") {
            e.preventDefault()
            const next = inputRefs.current[index + 1]
            if (next) next.focus()
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            const prev = inputRefs.current[index - 1]
            if (prev) prev.focus()
        }
    }



    // Check if product is selected
    const isSelected = (id) => selectedItems.some(item => item.productIdentifier === id)

    const updateStock = async () => {
        // console.log(selectedItems)
        setIsUpdatingStock(true)
        const response = await fetch(BASE_URL_API + "updateInventoryStock",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    allData: selectedItems,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                toast(`Stock has been successfully updated!`)
                getInventoryProducts()
                setSelectedItems([])
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                }
            }
        }
        setIsUpdatingStock(false)
    }

    const allSelected = filteredData.length > 0 && selectedItems.length === filteredData.length;

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedItems([]); // deselect all
        } else {
            setSelectedItems(filteredData); // select all
        }
    };

    const isReadyToUpload = () => {
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) return false;

        return selectedItems.every(item => {
            const v = item?.addQuantity;
            // must be present, numeric, and > 0
            if (v === null || v === undefined || v === '') return false;
            const n = Number(v);
            return Number.isFinite(n) && n > 0;
        });
    };

    useEffect(() => {
        setShouldUploadNow(isReadyToUpload())
    }, [selectedItems])




    if (activeStore === null) {
        return (
            <div className={styles.noStoreSelected} >
                Please select store first!
            </div>
        )
    }

    return (
        <div className={styles.mainWrapper} >
            <div className={styles.mainHeader}>
                <div className={styles.mainTitle} >
                    Add Stock
                </div>
                <div className={styles.needHelp}>
                    Need Help ?
                </div>
            </div>

            <div className={styles.messageTxtWrapper}>
                <div className={styles.messageTxt}>
                    <FaCheckCircle color="#276FDB" size={16} /> Products have been imported successfully. Now update stock level in bulk.
                </div>
            </div>

            <div className={styles.searchHeader}>
                <div className={styles.searchHeaderContainer} >
                    <div className={styles.inputWrapper} >
                        <input placeholder="Search products..." onChange={(e) => filterData(productsData, e.target.value)} />
                    </div>
                </div>
            </div>

            {productsData?.length > 0 ?
                <div className={styles.mainBodyWrapper}>
                    <div className={styles.mainBodyContainer}>
                        <div className={styles.tableHeader}>
                            <div className={styles.sn} onClick={() => toggleSelectAll()} >
                                {allSelected ? (
                                    <MdCheckBox size={18} color="#276FDB" />
                                ) : (
                                    <MdCheckBoxOutlineBlank size={18} />
                                )}
                            </div>
                            <div className={styles.productName}>
                                Product Name
                            </div>
                            <div className={styles.categoryName} >
                                Category
                            </div>
                            <div className={styles.barcode} >
                                SKU / Barcode
                            </div>
                            <div className={styles.mrp}>
                                Current Stock
                            </div>
                            <div className={styles.price}>
                                Add Quantity
                            </div>
                            <div className={styles.stock} >
                                Final Stock
                            </div>
                        </div>
                        <div className={styles.productWrapper}>
                            {filteredData?.map((item, index) => {
                                const selected = isSelected(item.productIdentifier)
                                const selectedData = selectedItems.find(p => p.productIdentifier === item.productIdentifier)

                                return (
                                    <div key={index} className={styles.eachRowWrapper} >
                                        <div className={styles.sn} onClick={() => toggleSelect(item)}>
                                            {selected ? <MdCheckBox size={18} color="#276FDB" /> : <MdCheckBoxOutlineBlank size={18} />}
                                        </div>
                                        <div className={styles.productName} onClick={() => toggleSelect(item)}>
                                            {item?.productName}
                                        </div>
                                        <div className={styles.categoryName}>
                                            {item?.categoryName}
                                        </div>
                                        <div className={styles.barcode}>
                                            {item?.skuCode || item?.barCode}
                                        </div>
                                        <div className={styles.mrp}>
                                            {item?.quantity}
                                        </div>
                                        <div className={styles.price}>
                                            {selected ? (
                                                <input
                                                    ref={el => (inputRefs.current[index] = el)}
                                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                                    type="number"
                                                    value={selectedData?.addQuantity || ""}
                                                    onChange={(e) => handleQuantityChange(item.productIdentifier, e.target.value)}
                                                />
                                            ) : (
                                                <span className={styles.disabledInput}>--</span>
                                            )}
                                        </div>
                                        <div className={styles.stock}>
                                            {selected ? (parseFloat(item?.quantity || 0) + parseFloat(selectedData?.addQuantity || 0)) : parseFloat(item?.quantity || 0)}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
                :
                <div className={styles.mainBodyWrapper}>
                    <div className={styles.noProduct} >
                        You have not added any product in the inventory yet!
                    </div>
                </div>
            }

            <div className={styles.actionWrapper} >
                <div className={styles.actionContainer} >
                    <div>
                        {selectedItems.length > 0 && <span>{selectedItems.length} items selected</span>}
                    </div>
                    <div>
                        {shouldUploadNow === true ?
                            <>
                                {isUpdatingStock === true ?
                                    <button className={styles.updateBtnLoader} disabled={isUpdatingStock} >
                                        <BiLoaderAlt size={15} className={styles.spinner} /> Updating Stock...
                                    </button>
                                    :
                                    <button className={styles.updateBtn} onClick={() => updateStock()} >Confirm & Update</button>
                                }
                            </>
                            :
                            <button className={styles.updateBtn} onClick={() => alert('Kindly provide quantity to all the select items.')} >Confirm & Update</button>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ImBulkAddStock
